import { useEffect, useState } from "react";
import { AuthNav } from "../../../components/Navbar/Webpages/AuthNav";
import { SideNav } from "../../../components/Navbar/Webpages/AuthNav";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { CircularProgress } from "@material-ui/core";

import { UpdatePasswordForm } from "../../../components/Form/UpdatePasswordForm";
import { UpdateUserForm } from "../../../components/Form/UpdateUserForm";
import { getLogUserRequest } from "../../../redux/action";
import {
  FaCheck,
  FaCoins,
  FaCopy,
  FaUserFriends,
  FaWallet,
  FaUniversity,
} from "react-icons/fa";

const Account = () => {
  const dispatch = useDispatch();

  const { user, getLogUserError, token, getLogUserLoading } = useSelector(
    (state) => {
      const {
        errors: { getPlans: getPlansError, getLogUser: getLogUserError },
      } = state.ajaxStatuses;

      const { getLogUserLoading } = state.loadingIndicator;

      const { user, token } = state.userData;

      return {
        user,
        getLogUserError,
        token,
        getLogUserLoading,
      };
    }
  );

  useEffect(() => {
    if (getLogUserError) {
      toast.error(getLogUserError, {
        duration: 3000,
      });
    }
  }, [getLogUserError]);

  useEffect(() => {
    dispatch(getLogUserRequest(token));
  }, [getLogUserRequest]);

  const [copied, setCopied] = useState(false);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "ZMW",
    }).format(amount || 0);
  };

  const handleCopy = () => {
    if (!user.walletAddress) return;
    navigator.clipboard.writeText(user.walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if bank details exist
  const hasBankDetails = user.bankName && user.accountNumber;

  return (
    <div className="h-screen overflow-y-auto">
      <AuthNav title="Settings" />
      <SideNav />
      <div>
        <div className="container max-w-screen-xl mx-auto px-3 pt-20">
          {getLogUserLoading ? (
            <div className="flex items-center justify-center py-20">
              <CircularProgress style={{ color: "black" }} size="20px" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border  overflow-hidden w-full max-w-sm mx-auto">
              {/* Decorative Header */}

              <div className="px-6 py-6 relative">
                {/* User Info */}
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 font-heading">
                    {user.userName || "User"}
                  </h2>
                  <p className="text-sm text-gray-500 mb-3">{user.email}</p>

                  {/* Crypto Wallet Chip */}
                  {user.walletAddress && (
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-100 rounded-full text-xs font-mono text-primary-600 hover:bg-primary-100 transition-colors group mb-2"
                    >
                      <FaWallet className="text-primary-400" />
                      <span>
                        {user.walletAddress.slice(0, 6)}...
                        {user.walletAddress.slice(-4)}
                      </span>
                      {copied ? (
                        <FaCheck className="text-green-500" />
                      ) : (
                        <FaCopy className="text-primary-300 group-hover:text-primary-500" />
                      )}
                    </button>
                  )}
                </div>

                {/* BANK DETAILS CARD (New Addition) */}
                {hasBankDetails && (
                  <div className="mb-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
                    {/* Decorative circle */}
                    <div className="absolute top-0 right-0 -mr-4 -mt-4 w-16 h-16 bg-white opacity-5 rounded-full blur-xl"></div>

                    <div className="flex items-center gap-2 mb-4 opacity-70">
                      <FaUniversity className="text-sm" />
                      <span className="text-xs font-bold uppercase tracking-wider">
                        Bank Details
                      </span>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">
                        Account Number
                      </p>
                      <p className="text-lg font-mono tracking-widest">
                        {user.accountNumber}
                      </p>
                    </div>

                    <div className="mt-4 flex justify-between items-end">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">
                          Account Name
                        </p>
                        <p className="text-sm font-medium truncate max-w-[150px]">
                          {user.accountName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400 uppercase">
                          Bank
                        </p>
                        <p className="text-sm font-bold text-primary-200">
                          {user.bankName}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats Divider */}
                <div className="border-t  my-4"></div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-3 rounded-xl bg-gray-50 border ">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                      <FaCoins className="text-yellow-500" /> Balance
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatMoney(user.balance)}
                    </p>
                  </div>

                  <div className="p-3 rounded-xl bg-gray-50 border ">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                      <FaUserFriends className="text-primary-500" /> Referrals
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {user.referralCount || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="lg:mt-16 max-w-screen-lg md:mx-auto mt-8 mx-2 grid grid-cols-1 md:grid-cols-2 gap-x-4  gap-y-8">
            <UpdateUserForm />
            <UpdatePasswordForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
